import { useEffect } from 'react';
import { useIsParentOfficeSelector } from '~/hooks/useIsParentOfficeSelector';

interface IUseDefaultRecommendedRateParams {
  /** Current rate value. When `null`/`undefined` the rate may be defaulted. */
  value?: number | null;
  /** Office the shift is being created for. Empty until a parent picks an account. */
  officeId: string;
  /** The office's recommended (average) rate, or `null` when no recommendation exists yet. */
  recommendedRate: number | null;
  /** Form setter for the rate field. */
  onChange?: (value: number) => void;
  /** Receives the same value at the moment it prefills the rate (GOTU-7584). */
  onRecommendedRateChange?: (recommendedRate: number) => void;
}

/**
 * Defaults the shift-request rate to the office's recommended (average) rate.
 *
 * - Child office: its `officeId` is auto-populated when the form opens, so the
 *   rate defaults to the recommendation on open.
 * - Parent office: no account is selected on open (`officeId` is empty), so the
 *   rate stays `null`; it defaults once the parent selects a child account.
 *
 * When the office or profession type changes, `ShiftRequestLayout` resets the
 * rate to `null` and this hook re-applies the recommendation for the new
 * selection.
 */
export const useDefaultRecommendedRate = ({
  value,
  officeId,
  recommendedRate,
  onChange,
  onRecommendedRateChange,
}: IUseDefaultRecommendedRateParams) => {
  const isParentOffice = useIsParentOfficeSelector();

  useEffect(() => {
    // Only default when the rate is empty and we have a real recommendation.
    if (value != null || recommendedRate == null) return;

    // A parent office must explicitly select a child account before we apply a
    // recommendation; its `officeId` stays empty on open. A child office's own
    // `officeId` is already populated, so it defaults on open.
    if (isParentOffice && !officeId) return;

    onChange?.(recommendedRate);
    onRecommendedRateChange?.(recommendedRate);
  }, [value, officeId, recommendedRate, isParentOffice, onChange, onRecommendedRateChange]);
};
